import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CircleIcon from "@mui/icons-material/Circle";

import WetWaste from "../../Assests/SVG/WetWaste.svg";
import DryWaste from "../../Assests/SVG/DryWaste.svg";
import DomesticHazardousWaste from "../../Assests/SVG/DomesticHazardousWaste.svg";
import { NameValue, OverviewChart } from "../../Models/Models";
import {
  GetWasteCardData,
  GetTotalBinCardData,
  GetTotalWasteCardData,
} from "../../ApiServices/ApiRequests";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useMasterData } from "../../ContextAPI/MasterContext";

const useStyles = makeStyles(() => ({
  card: {
    textAlign: "center",
    borderRadius: "12px",
    backgroundColor: "#fff",
    height: "480px",
    marginTop: "20px",
  },
  totalBins: {
    fontSize: "50px", // Large number size
    fontWeight: "bold",
    color: "#8850ef", // Purple color
  },
  wasteType: {
    display: "flex",
    justifyContent: "space-around",
  },
  wasteLabel: {
    textAlign: "center",
    color: "#757575",
    fontSize: "12px",
  },
  categoryFilter: {
    marginTop: "15px",
    padding: "15px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#8850ef",
    width: "85%",
    marginLeft: 30,
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",

    fontSize: "2px",
  },
  listIcon: {
    fontSize: "12px", // Adjust icon size
  },
  percentage: {
    marginLeft: "8px",
    color: "#757575",
    fontSize: "12px",
  },
  wasteValue: {
    color: "#8850ef", // Same purple color for waste values
    fontSize: "20px", // Value size
    fontWeight: "bold",
  },
  scrollableSection: {
    maxHeight: "95px", // Set max-height
    overflowY: "auto", // Enable vertical scrolling
    "&::-webkit-scrollbar": {
      width: "3px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#ffffff",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#8850EF",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#c4c4c4",
    },
  },
}));

const WasteMonitoringCard: React.FC = () => {
  const{masterState}=useMasterData();
  const classes = useStyles();

  const [loading, setLoading] = useState(true);

  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value: string | boolean | number | null | NameValue[] | OverviewChart
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkChange = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );

  // Fetch bin card data when the component mounts
  useEffect(() => {
    const fetchWasteCardData = async (
      siteCode: string,
      date: string
    ): Promise<void> => {
      try {
        const data: NameValue[] = await GetWasteCardData(siteCode, date);
        handleInputChange("wasteCollectionAvgData", data);
      } catch (err) {
        console.error("Error fetching bin card data:", err);
      } finally {
        setLoading(false);
      }
    };

    const formattedDate: string = masterState.selectedDate
      ? masterState.selectedDate.format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    if (
      masterState.selectedSiteCode.length > 0 &&
      masterState.selectedDate
    ) {
      fetchWasteCardData(masterState.selectedSiteCode, formattedDate);
    }
  }, [
    masterState.selectedSiteCode,
    masterState.selectedDate,
  ]);

  // Fetch total bin card data
  useEffect(() => {
    const fetchTotalWasteCardData = async (
      siteCode: string,
      date: string
    ): Promise<void> => {
      try {
        const data: OverviewChart = await GetTotalWasteCardData(siteCode, date);
        handleInputChange("wasteCollectionType", data);
      } catch (err) {
        console.error("Error fetching total bin card data:", err);
      } finally {
        setLoading(false);
      }
    };

    const formattedDate: string = masterState.selectedDate
      ? masterState.selectedDate.format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    if (
      masterState.selectedSiteCode.length > 0 &&
      masterState.selectedDate
    ) {
      fetchTotalWasteCardData(
        masterState.selectedSiteCode,
        formattedDate
      );
    }
  }, [
    masterState.selectedSiteCode,
    masterState.selectedDate,
  ]);

  if (loading) return <CircularProgress />;
  if (loading) {
    return (
      <Card className={classes.card}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          minHeight="200px" // Adjust as needed for visual centering
        >
          <CircularProgress />
        </Box>
      </Card>
    );
  }
  const imageSources = [WetWaste, DryWaste, DomesticHazardousWaste]; // Array of image sources in order

  return (
    <Card className={classes.card} style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Total Bins Monitoring */}

      <Typography variant="h6" color="textSecondary" mt={5}>
        {t("global.TotalWasteGenerated")}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {t("global.VolumeInKg")}
      </Typography>
      <Typography className={classes.totalBins} variant="h3">
        {wasteManagementState.wasteCollectionType?.xaxes}
      </Typography>

      <Box className={classes.wasteType}>
        {wasteManagementState.wasteCollectionType?.yaxes?.map(
          (waste, index) => {
            // Set image based on index
            const imageSrc = imageSources[index % imageSources.length];
            return (
              <Box key={index} style={{ margin: "5px" }}>
                <img
                  src={imageSrc}
                  alt={waste.name}
                  style={{ width: "50px", height: "50px" }}
                />
                <Typography className={classes.wasteLabel}>
                  {waste.name}
                </Typography>
                <Typography className={classes.wasteValue} variant="h5">
                  {waste.value}
                </Typography>
              </Box>
            );
          }
        )}
      </Box>

      <Divider sx={{ marginTop: "20px" }} />

      {/* Category Filter */}
      <Box className={classes.categoryFilter}>
        <CircleIcon sx={{ fontSize: 12, marginRight: "8px" }} />
        <Typography variant="subtitle1" style={{ fontSize: "14px" }}>
          {t("global.AllCategories")}
        </Typography>
      </Box>
      <div
        className={classes.scrollableSection} // Add class for WebKit browsers
      >
        <List>
          {wasteManagementState.wasteCollectionAvgData &&
          wasteManagementState.wasteCollectionAvgData.length > 0 ? (
            wasteManagementState.wasteCollectionAvgData?.map((bin, index) => (
              <div key={index} className={classes.listItem}>
                <div className="" style={{display:"flex"}}>
                <CircleOutlinedIcon
                  sx={{
                    color: Number(bin.value) > 0 ? "#4caf50" : "#f44336",
                    fontSize: "20px",
                    marginLeft:"25px"
                  }}
                />

                <Typography
                  style={{ fontSize: "14px", wordWrap: "break-word",marginLeft:"5px" }}
                >
                  {bin.name}
                </Typography>
                </div>
                <Typography
                  className={classes.percentage}
                  style={{
                    fontSize: "15px",
                    marginRight: "25px",
                    wordWrap: "break-word",
                  }}
                >
                  {bin.value ? bin.value : "0"}%
                </Typography>
              </div>
            ))
          ) : (
            <Typography
              variant="h6"
              color="textSecondary"
              align="center"
              style={{ padding: "0px" }}
            >
              {t("global.NoDataFound")}
            </Typography>
          )}
        </List>
      </div>
      {/* <Typography
        variant="caption"
        color="textSecondary"
        style={{ marginTop: "10px" }}
      >
        {t("global.Volumeinkg")}
      </Typography> */}
      <div style={{ marginTop: 'auto' }}>
      <Typography variant="caption" color="textSecondary">
      {t("global.Volumeinkg")}
      </Typography>
    </div>
    </Card>
  );
};

export default WasteMonitoringCard;
